import { LegalPage } from "../../components/LegalPage";

const EFFECTIVE = "2026-05-30";

export function Security() {
  return (
    <LegalPage
      title="Security"
      eyebrow="Trust center"
      description="WeConsult Rx security overview: encryption, access control, MFA, audit logging, RLS pen-testing, vulnerability management, and incident response."
      path="/security"
      effective={EFFECTIVE}
    >
      <p>
        WeConsult Rx is built for healthcare. Security and privacy are not
        bolted on — they are enforced by the code, the database, and the test
        suite that gates every release.
      </p>

      <h2>Encryption</h2>
      <ul>
        <li>
          <strong>At rest:</strong> AES-256 on Supabase Postgres and Supabase
          Storage. Per-tenant Storage prefixes prevent cross-tenant object
          enumeration.
        </li>
        <li>
          <strong>In transit:</strong> TLS 1.2+ on every endpoint. HSTS enforced
          on `weconsultrx.com` and `app.weconsultrx.com`.
        </li>
        <li>
          <strong>Tenant secrets:</strong> Supabase Vault with per-tenant secret
          names. Secrets are never returned to the client; we only round-trip
          pointers.
        </li>
      </ul>

      <h2>Access control</h2>
      <ul>
        <li>
          Multi-factor authentication enforced for staff accounts on the
          platform.
        </li>
        <li>
          Role-based access (owner · admin · reviewer · staff) with row-level
          security (RLS) on every PHI table.
        </li>
        <li>
          Cross-tenant access blocked by Postgres RLS — verified by a pen-test
          suite of 31+ probes run on every commit.
        </li>
        <li>
          Service-role keys are never present in browser bundles.
        </li>
      </ul>

      <h2>Audit logging</h2>
      <p>
        Every action that touches PHI is logged to an append-only
        <code>audit_log</code> table. Logs include actor, action, resource, and
        sanitized metadata. PHI is excluded from metadata by gate tests.
      </p>
      <ul>
        <li>Retention: 7 years (HIPAA §164.530(j))</li>
        <li>Export: tenant admins can export their tenant’s audit slice via signed link.</li>
        <li>Rate limit: 5 audit exports per hour per tenant to prevent exfiltration.</li>
      </ul>

      <h2>Application security</h2>
      <ul>
        <li>
          Static type checking (TypeScript strict mode +
          <code>exactOptionalPropertyTypes</code>) on every package.
        </li>
        <li>
          Dependency scanning on every pull request.
        </li>
        <li>
          Honeypot + rate limiting on every public form.
        </li>
        <li>
          Adobe Sign webhooks verified by HMAC. Resend and Twilio webhooks
          verified by signature.
        </li>
      </ul>

      <h2>Data residency</h2>
      <p>
        Primary database and storage in AWS us-west (Oregon) via Supabase.
        AI inference via AWS Bedrock in HIPAA-eligible regions only.
      </p>

      <h2>Vulnerability management</h2>
      <p>
        Security researchers — please see our{" "}
        <a href="/responsible-disclosure">responsible disclosure policy</a> and
        email security@weconsultrx.com.
      </p>

      <h2>Incident response</h2>
      <p>
        Security incidents are triaged within one business hour during US business
        hours. Customers are notified per BAA terms (no later than 60 days for
        confirmed breaches; sooner for material incidents). Post-incident reports
        are shared with affected customers.
      </p>

      <h2>Penetration testing</h2>
      <p>
        Internal pen-test suite runs on every commit. Third-party penetration
        test scheduled annually beginning Q1 2027. Customers under Professional
        and Enterprise tiers may request a summary letter.
      </p>
    </LegalPage>
  );
}
